import { redirect } from "@tanstack/react-router";
import { can } from "@/lib/staff-roles";
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  CheckCircle2,
  Image as ImageIcon,
  KeyRound,
  Layers,
  RefreshCw,
  ShieldAlert,
  ShieldCheck,
  Undo2,
  XCircle,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/admin/activity")({
  beforeLoad: ({ context }) => {
    if (!can(context.staffRole, "viewBookings")) {
      throw redirect({ to: "/admin/calendar" });
    }
  },
  head: () => ({
    meta: [
      { title: "Admin Activity Log — Coffman Cove Lodging" },
      { name: "description", content: "Recent staff sign-ins and administrative changes." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminActivity,
});

type Category = "sign-in" | "photo-review" | "bulk-action";

type Event = {
  id: string;
  at: string;
  category: Category;
  actor: string;
  title: string;
  detail: string | null;
  tone: "ok" | "warn" | "bad" | "neutral";
};

const LOOKBACK_DAYS = 30;
const MAX_PER_SOURCE = 200;

const FILTERS: { key: Category | "all"; label: string }[] = [
  { key: "all", label: "All activity" },
  { key: "sign-in", label: "Sign-ins" },
  { key: "photo-review", label: "Photo reviews" },
  { key: "bulk-action", label: "Bulk actions" },
];

function iconFor(event: Event) {
  if (event.category === "sign-in") {
    return event.tone === "ok" ? ShieldCheck : event.tone === "bad" ? ShieldAlert : KeyRound;
  }
  if (event.category === "bulk-action") return event.title.startsWith("Undo") ? Undo2 : Layers;
  return event.tone === "ok" ? CheckCircle2 : event.tone === "bad" ? XCircle : ImageIcon;
}

function toneClass(tone: Event["tone"]) {
  if (tone === "ok") return "text-emerald-400 border-emerald-400/40 bg-emerald-400/10";
  if (tone === "bad") return "text-destructive border-destructive/40 bg-destructive/10";
  if (tone === "warn") return "text-[var(--color-ember)] border-[var(--color-ember)]/40 bg-[var(--color-ember)]/10";
  return "text-muted-foreground border-border bg-muted/30";
}

function formatWhen(iso: string) {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function AdminActivity() {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Category | "all">("all");

  const load = useCallback(async () => {
    setLoading(true);
    const since = new Date(Date.now() - LOOKBACK_DAYS * 86_400_000).toISOString();
    try {
      const [logins, reviews, bulk] = await Promise.all([
        supabase
          .from("admin_login_attempts")
          .select("id, email, succeeded, created_at")
          .gte("created_at", since)
          .order("created_at", { ascending: false })
          .limit(MAX_PER_SOURCE),
        supabase
          .from("submission_review_audit")
          .select("id, submission_id, action, reviewer_email, created_at")
          .gte("created_at", since)
          .order("created_at", { ascending: false })
          .limit(MAX_PER_SOURCE),
        supabase
          .from("admin_activity_log")
          .select("id, actor_email, action, submission_count, created_at")
          .gte("created_at", since)
          .order("created_at", { ascending: false })
          .limit(MAX_PER_SOURCE),
      ]);

      const firstError = logins.error ?? reviews.error ?? bulk.error;
      if (firstError) throw firstError;

      const merged: Event[] = [
        ...(logins.data ?? []).map((row) => ({
          id: `login-${row.id}`,
          at: row.created_at,
          category: "sign-in" as const,
          actor: row.email,
          title: row.succeeded ? "Successful sign-in" : "Failed sign-in attempt",
          detail: null,
          tone: row.succeeded ? ("ok" as const) : ("bad" as const),
        })),
        ...(reviews.data ?? []).map((row) => ({
          id: `review-${row.id}`,
          at: row.created_at,
          category: "photo-review" as const,
          actor: row.reviewer_email ?? "Unknown staff",
          title: row.action === "approved" ? "Approved a photo" : row.action === "rejected" ? "Rejected a photo" : "Reset a photo to pending",
          detail: `Submission ${row.submission_id.slice(0, 8)}`,
          tone: row.action === "approved" ? ("ok" as const) : row.action === "rejected" ? ("bad" as const) : ("neutral" as const),
        })),
        ...(bulk.data ?? []).map((row) => ({
          id: `bulk-${row.id}`,
          at: row.created_at,
          category: "bulk-action" as const,
          actor: row.actor_email ?? "Unknown staff",
          title:
            row.action === "bulk_approve"
              ? "Bulk approved photos"
              : row.action === "bulk_reject"
                ? "Bulk rejected photos"
                : "Undid a bulk action",
          detail: `${row.submission_count} submission${row.submission_count === 1 ? "" : "s"}`,
          tone: row.action === "undo_bulk" ? ("warn" as const) : ("neutral" as const),
        })),
      ].sort((a, b) => b.at.localeCompare(a.at));

      setEvents(merged);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not load the activity log.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = useMemo(
    () => (filter === "all" ? events : events.filter((e) => e.category === filter)),
    [events, filter],
  );

  const stats = useMemo(() => {
    const day = Date.now() - 86_400_000;
    const recent = events.filter((e) => new Date(e.at).getTime() >= day);
    return {
      signInsToday: recent.filter((e) => e.category === "sign-in" && e.tone === "ok").length,
      failedToday: recent.filter((e) => e.category === "sign-in" && e.tone === "bad").length,
      changesToday: recent.filter((e) => e.category !== "sign-in").length,
    };
  }, [events]);

  return (
    <div className="mx-auto max-w-4xl px-4 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl">Activity Log</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Staff sign-ins and administrative changes from the last {LOOKBACK_DAYS} days.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-medium transition-colors hover:border-[var(--color-ember)] hover:text-[var(--color-ember)] disabled:opacity-60"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : undefined} aria-hidden />
          Refresh
        </button>
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-3">
        <StatCard label="Sign-ins (24h)" value={stats.signInsToday} />
        <StatCard label="Failed attempts (24h)" value={stats.failedToday} alert={stats.failedToday > 0} />
        <StatCard label="Changes (24h)" value={stats.changesToday} />
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={
              filter === f.key
                ? "rounded-full border border-[var(--color-ember)] bg-[var(--color-ember)] px-4 py-1.5 text-sm font-semibold text-[var(--color-ember-foreground)]"
                : "rounded-full border border-border px-4 py-1.5 text-sm text-muted-foreground transition-colors hover:border-[var(--color-ember)] hover:text-[var(--color-ember)]"
            }
          >
            {f.label}
          </button>
        ))}
      </div>

      <ol className="mt-6 space-y-2">
        {loading && events.length === 0 ? (
          <li className="rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">Loading activity…</li>
        ) : visible.length === 0 ? (
          <li className="rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">
            No activity recorded in this range.
          </li>
        ) : (
          visible.map((event) => {
            const Icon = iconFor(event);
            return (
              <li
                key={event.id}
                className="flex items-start gap-3 rounded-lg border border-border bg-card px-4 py-3"
              >
                <span className={`mt-0.5 rounded-full border p-1.5 ${toneClass(event.tone)}`}>
                  <Icon size={15} aria-hidden />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{event.title}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {event.actor}
                    {event.detail ? ` · ${event.detail}` : ""}
                  </p>
                </div>
                <time
                  dateTime={event.at}
                  title={new Date(event.at).toLocaleString()}
                  className="shrink-0 text-xs tabular-nums text-muted-foreground"
                >
                  {formatWhen(event.at)}
                </time>
              </li>
            );
          })
        )}
      </ol>
    </div>
  );
}

function StatCard({ label, value, alert }: { label: string; value: number; alert?: boolean }) {
  return (
    <div className="rounded-lg border border-border bg-card px-4 py-3">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`mt-1 font-display text-2xl ${alert ? "text-destructive" : ""}`}>{value}</p>
    </div>
  );
}
